import '../../styles/viz.css';
import { Fragment, useEffect, useId, useMemo, useRef, useState, type PointerEvent, type ReactNode, type Ref } from 'react';

/*
 * Animated charts for the violet theme. Every chart draws itself in on mount (and again when its data set
 * changes, via `key`), carries a hover layer where it plots values, and ships a visually hidden table so the
 * numbers are never colour- or motion-only. Motion is CSS: `prefers-reduced-motion` turns it off globally.
 */

export interface XY {
  x: number;
  y: number;
}

function scales(points: XY[], W: number, H: number, pad: { l: number; r: number; t: number; b: number }, yFloor?: number) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  let y0 = yFloor ?? Math.min(...ys);
  let y1 = Math.max(...ys);
  if (y1 - y0 < 1e-12) {
    y0 -= 0.01;
    y1 += 0.01;
  }
  const span = y1 - y0;
  y1 += span * 0.12;
  if (yFloor === undefined) y0 -= span * 0.05;
  const sx = (x: number) => pad.l + ((x - x0) / (x1 - x0 || 1)) * (W - pad.l - pad.r);
  const sy = (y: number) => H - pad.b - ((y - y0) / (y1 - y0)) * (H - pad.t - pad.b);
  return { sx, sy, y0, y1 };
}

/**
 * Monotone cubic path through the points (Fritsch–Carlson, as d3's curveMonotoneX): it passes exactly through every
 * value and never overshoots between two of them, so the line shows what the data says (a Catmull-Rom spline bulges
 * past a jump and wobbles on the flat after it).
 */
export function monotonePath(pts: [number, number][]): string {
  const n = pts.length;
  if (n === 0) return '';
  const f = (v: number) => v.toFixed(1);
  if (n < 3) return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${f(x)},${f(y)}`).join('');
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1][0] - pts[i][0]);
    slope.push(dx[i] === 0 ? 0 : (pts[i + 1][1] - pts[i][1]) / dx[i]);
  }
  const m: number[] = [slope[0]];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1];
    const b = slope[i];
    // a turning point (or a flat side) gets a flat tangent; otherwise the weighted harmonic mean of the slopes
    m.push(a * b <= 0 ? 0 : (3 * (dx[i - 1] + dx[i])) / ((2 * dx[i] + dx[i - 1]) / a + (dx[i] + 2 * dx[i - 1]) / b));
  }
  m.push(slope[n - 2]);
  let d = `M${f(pts[0][0])},${f(pts[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const h = dx[i] / 3;
    d += `C${f(x0 + h)},${f(y0 + m[i] * h)} ${f(x1 - h)},${f(y1 - m[i + 1] * h)} ${f(x1)},${f(y1)}`;
  }
  return d;
}

/** Round grid values (steps of 1, 2, 2.5 or 5 × 10^k) inside [lo, hi], about `count` of them. */
export function niceTicks(lo: number, hi: number, count = 4): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * mag).find((s) => s >= raw) ?? 10 * mag;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

/** Index of the point whose x is closest to `x` (points sorted by x): a binary search, cheap on every pointer move. */
export function nearestIndex(xs: number[], x: number): number {
  let lo = 0;
  let hi = xs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] < x) lo = mid;
    else hi = mid;
  }
  return Math.abs(xs[hi] - x) < Math.abs(xs[lo] - x) ? hi : lo;
}

/**
 * Pointer hover for a chart: one update per animation frame, and only when the nearest point changes, so moving
 * across the chart never re-renders it more than the screen can show. Mouse, pen and touch alike.
 */
function useHover(xs: number[] | null, W: number) {
  const ref = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const frame = useRef(0);
  const lastX = useRef(0);
  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  const move = (clientX: number) => {
    lastX.current = clientX;
    if (frame.current || !xs) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const r = ref.current?.getBoundingClientRect();
      if (!r || r.width === 0) return;
      const i = nearestIndex(xs, ((lastX.current - r.left) / r.width) * W);
      setHover((h) => (h === i ? h : i));
    });
  };
  const leave = () => {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    setHover(null);
  };
  const handlers = {
    onPointerMove: (e: { clientX: number }) => move(e.clientX),
    onPointerDown: (e: { clientX: number }) => move(e.clientX),
    onPointerLeave: leave,
    onPointerCancel: leave,
  };
  return { ref, hover, handlers };
}

/** Where a tooltip sits so it stays inside the chart: centred, or pinned to the near edge; below the point near the top. */
export function tipPlacement(x: number, y: number, W: number): string {
  const tx = x < W * 0.18 ? '-12%' : x > W * 0.82 ? '-88%' : '-50%';
  const ty = y < 44 ? '35%' : '-118%';
  return `translate(${tx}, ${ty})`;
}

/** The rendered width of an element, so SVG text keeps its true size instead of scaling with the card. */
function useWidth<T extends Element>(fallback: number) {
  const ref = useRef<T>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(120, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

function SrTable({ label, rows }: { label: string; rows: [string, string][] }) {
  return (
    // the wrapper carries sr-only: a <table> ignores height and overflow, so hiding the table itself still
    // lays out every row and stretches the page
    <div className="sr-only">
      <table>
        <caption>{label}</caption>
        <tbody>
          {rows.map(([a, b], i) => (
            <tr key={i}>
              <th scope="row">{a}</th>
              <td>{b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A trend line drawn at its real pixel size (no stretching). `points` (the reference design): straight segments with
 * a glowing vertex at every value, the vertices popping in one after another behind the line; otherwise a smooth
 * curve with a soft area. Either way the last value glows and pulses. `titles` label each vertex on hover.
 */
export function Sparkline({
  values,
  label,
  height = 56,
  variant = 'smooth',
  titles,
}: {
  values: number[];
  label: string;
  height?: number;
  variant?: 'smooth' | 'points';
  titles?: string[];
}) {
  const [box, W] = useWidth<HTMLDivElement>(320);
  if (variant === 'points') return <PointsLine box={box} W={W} H={height} values={values} label={label} titles={titles} />;
  return <SmoothLine box={box} W={W} H={height} values={values} label={label} />;
}

// Measured from the client's rebase-growth design (a 524 px crop, vertices spanning 415 px): the line runs
// violet → orchid → pink, fades in ~5% before the first vertex and out ~9% after the last; the stroke is ~3 px with
// a soft glow that is stronger below the line; vertices are ~5 px near-white dots tinted by the line beneath them.
const LINE_STOPS: [number, string][] = [
  [0, '#7e54d8'],
  [0.28, '#ad6fed'],
  [0.5, '#dd83ea'],
  [0.66, '#e47aef'],
  [0.84, '#eb8ddc'],
  [1, '#df93cb'],
];
const DOT_STOPS: [number, string][] = [
  [0, '#e0c3fd'],
  [0.4, '#f1d6ff'],
  [0.5, '#fee7fe'],
  [0.75, '#fec6fc'],
  [1, '#febafe'],
];

/** The rebase-growth line: straight segments between epochs, a small glowing vertex on each, fading tails. */
function PointsLine({ box, W, H, values, label, titles }: { box: Ref<HTMLDivElement>; W: number; H: number; values: number[]; label: string; titles?: string[] }) {
  const id = useId().replace(/:/g, '');
  const tailL = Math.round(W * 0.05);
  const tailR = Math.round(W * 0.09);
  const pts = useMemo(() => {
    if (values.length < 2 || !values.every(Number.isFinite)) return [];
    // the design keeps the zigzag in a band through the middle of the card
    const band = Math.round(H * 0.28);
    const s = scales(values.map((y, x) => ({ x, y })), W, H, { l: tailL + 2, r: tailR + 2, t: band, b: band });
    return values.map((y, x) => [s.sx(x), s.sy(y)] as [number, number]);
  }, [values, H, W, tailL, tailR]);
  const { ref: svgRef, hover: rawHover, handlers } = useHover(pts.length >= 2 ? pts.map((q) => q[0]) : null, W);
  if (pts.length < 2) return <div ref={box} style={{ height: H }} />;
  const hover = rawHover !== null && rawHover < pts.length ? rawHover : null;
  const first = pts[0];
  const end = pts[pts.length - 1];
  const x0 = first[0] - tailL;
  const x1 = end[0] + tailR;
  const at = (x: number) => (x - x0) / (x1 - x0); // a vertex's place on the gradient
  const k = Math.max(0.6, Math.min(1.2, (end[0] - first[0]) / 415)); // our scale against the design's
  const d = `M${x0.toFixed(1)},${first[1].toFixed(1)}` + pts.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join('') + `L${x1.toFixed(1)},${end[1].toFixed(1)}`;
  const a = at(first[0]);
  const b = at(end[0]);
  const step = 1.2 / pts.length; // the vertices follow the line's 1.5 s draw
  // userSpaceOnUse: a flat line has a zero-height bounding box, on which an objectBoundingBox gradient paints nothing
  const grad = (gid: string, stops: [number, string][], fade: boolean) => (
    <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={x0} y1="0" x2={x1} y2="0">
      {fade ? <stop offset="0" stopColor={stops[0][1]} stopOpacity="0" /> : null}
      {stops.map(([o, c]) => (
        <stop key={o} offset={a + o * (b - a)} stopColor={c} />
      ))}
      {fade ? <stop offset="1" stopColor={stops[stops.length - 1][1]} stopOpacity="0" /> : null}
    </linearGradient>
  );
  return (
    <div ref={box} className="spark-box">
      <svg
        ref={svgRef}
        className="spark"
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        role="img"
        aria-label={label}
        key={`${W}:${values.join(',')}`}
        {...handlers}
      >
        <defs>
          {grad(`pl${id}`, LINE_STOPS, true)}
          {grad(`pd${id}`, DOT_STOPS, false)}
          <filter id={`pg${id}`} x="-5%" y="-100%" width="110%" height="300%">
            <feGaussianBlur stdDeviation={2.6 * k} />
          </filter>
          <filter id={`ph${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={1.4 * k} />
          </filter>
        </defs>
        {/* soft glow, a little stronger beneath the line */}
        <path d={d} className="draw" fill="none" stroke={`url(#pl${id})`} strokeWidth={6 * k} strokeOpacity="0.32" transform={`translate(0 ${1.6 * k})`} filter={`url(#pg${id})`} />
        <path d={d} className="draw" fill="none" stroke={`url(#pl${id})`} strokeWidth={4 * k} strokeOpacity="0.45" filter={`url(#pg${id})`} />
        <path d={d} className="draw" fill="none" stroke={`url(#pl${id})`} strokeWidth={2.3 * k} strokeLinecap="round" strokeLinejoin="round" />
        {pts.map(([x, y], i) => (
          <g key={i} className="vertex" style={{ animationDelay: `${0.15 + i * step}s` }}>
            <circle cx={x} cy={y} r={3.6 * k} fill={`url(#pd${id})`} opacity="0.55" filter={`url(#ph${id})`} />
            <circle cx={x} cy={y} r={2.2 * k} fill={`url(#pd${id})`} />
          </g>
        ))}
        {hover !== null ? (
          <circle className="crosshair-dot" cx={0} cy={0} r={4.5 * k} fill="#fff" stroke="#e47aef" strokeWidth={2} style={{ transform: `translate(${pts[hover][0]}px, ${pts[hover][1]}px)` }} />
        ) : null}
        <rect x="0" y="0" width={W} height={H} fill="transparent" />
      </svg>
      {hover !== null && titles?.[hover] ? (
        <div
          className="chart-tip glide"
          role="status"
          data-testid="spark-tip"
          style={{ left: `${(pts[hover][0] / W) * 100}%`, top: `${(pts[hover][1] / H) * 100}%`, transform: tipPlacement(pts[hover][0], pts[hover][1] + 44, W) }}
        >
          {titles[hover]}
        </div>
      ) : null}
    </div>
  );
}

/** A smooth line with a gradient area beneath it and a pulsing end point. */
function SmoothLine({ box, W, H, values, label }: { box: Ref<HTMLDivElement>; W: number; H: number; values: number[]; label: string }) {
  const id = useId().replace(/:/g, '');
  const pts = useMemo(() => {
    if (values.length < 2 || !values.every(Number.isFinite)) return [];
    const s = scales(values.map((y, x) => ({ x, y })), W, H, { l: 6, r: 10, t: 12, b: 10 });
    return values.map((y, x) => [s.sx(x), s.sy(y)] as [number, number]);
  }, [values, H, W]);
  if (pts.length < 2) return <div ref={box} style={{ height: H }} />;
  const d = monotonePath(pts);
  const end = pts[pts.length - 1];
  const area = `${d}L${end[0].toFixed(1)},${H}L${pts[0][0].toFixed(1)},${H}Z`;
  return (
    <div ref={box} className="spark-box">
      <svg className="spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={label} key={`${W}:${values.join(',')}`}>
        <defs>
          <linearGradient id={`sg${id}`} gradientUnits="userSpaceOnUse" x1={pts[0][0]} y1="0" x2={end[0]} y2="0">
            <stop offset="0" stopColor="#6d3cf5" stopOpacity="0.35" />
            <stop offset="0.55" stopColor="#8f4dff" />
            <stop offset="1" stopColor="#d94fe9" />
          </linearGradient>
          <linearGradient id={`sa${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8340fa" stopOpacity="0.35" />
            <stop offset="1" stopColor="#8340fa" stopOpacity="0" />
          </linearGradient>
          <filter id={`sf${id}`} x="-10%" y="-60%" width="120%" height="220%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <radialGradient id={`sv${id}`}>
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.45" stopColor="#f1c8ff" />
            <stop offset="1" stopColor="#c45cff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <path d={area} fill={`url(#sa${id})`} className="area-rise" />
        <path d={d} className="draw" fill="none" stroke={`url(#sg${id})`} strokeWidth="6" opacity="0.45" filter={`url(#sf${id})`} />
        <path d={d} className="draw" fill="none" stroke={`url(#sg${id})`} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
        <circle className="pulse" cx={end[0]} cy={end[1]} r="4" fill="none" stroke="#e684f5" strokeWidth="1.5" />
        <g className="pop-late">
          <circle cx={end[0]} cy={end[1]} r="8" fill={`url(#sv${id})`} />
          <circle cx={end[0]} cy={end[1]} r="3.4" fill="#ffffff" />
        </g>
      </svg>
    </div>
  );
}

/** The steps of a ring's thickness, deepest first: each is the ring again, shifted down and darker. */
const DEPTH = [7, 6, 5, 4, 3, 2, 1];

/** A ring that sweeps to `fraction` with a violet→pink gradient and a glowing head. */
export function RingGauge({ fraction, size = 200, stroke = 16, children, label }: { fraction: number; size?: number; stroke?: number; children: ReactNode; label: string }) {
  const id = useId().replace(/:/g, '');
  const f = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0));
  const r = (size - stroke) / 2 - 6;
  const c = size / 2;
  const C = 2 * Math.PI * r;
  const ang = -Math.PI / 2 + f * 2 * Math.PI;
  return (
    <div className="ring" style={{ width: size, height: size }} role="img" aria-label={label} data-lean="vars">
      {/* a solid band: the ring drawn again below itself in darker steps, then the whole ring tilted back */}
      <div className="depth-body ring-body">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <defs>
          <linearGradient id={`rg${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8340fa" />
            <stop offset="0.55" stopColor="#d94fe9" />
            <stop offset="1" stopColor="#faa876" />
          </linearGradient>
          <filter id={`rf${id}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <filter id={`rd${id}`}>
            <feColorMatrix values="0.34 0 0 0 0  0 0.26 0 0 0  0 0 0.46 0 0  0 0 0 1 0" />
          </filter>
        </defs>
        {DEPTH.map((k) => (
          <g key={k} transform={`translate(0 ${k * 1.3})`} filter={`url(#rd${id})`} opacity={1 - k * 0.07}>
            <circle cx={c} cy={c} r={r} fill="none" stroke="#3a2a74" strokeWidth={stroke} />
            <g transform={`rotate(-90 ${c} ${c})`} display={f > 0 ? undefined : 'none'}>
              <circle className="ring-sweep" cx={c} cy={c} r={r} fill="none" stroke={`url(#rg${id})`} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${C * f} ${C}`} style={{ ['--ring-c' as string]: `${C}` }} />
            </g>
          </g>
        ))}
        <circle cx={c} cy={c} r={r} fill="none" stroke="#241848" strokeWidth={stroke} />
        {/* a zero-length round-capped dash would still paint a dot, so an empty ring draws no sweep */}
        <g transform={`rotate(-90 ${c} ${c})`} display={f > 0 ? undefined : 'none'}>
          <circle
            className="ring-sweep"
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={`url(#rg${id})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${C * f} ${C}`}
            style={{ ['--ring-c' as string]: `${C}` }}
            filter={`url(#rf${id})`}
            opacity="0.5"
          />
          <circle
            className="ring-sweep"
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={`url(#rg${id})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${C * f} ${C}`}
            style={{ ['--ring-c' as string]: `${C}` }}
          />
        </g>
        {f > 0 ? <circle className="pop-late" cx={c + r * Math.cos(ang)} cy={c + r * Math.sin(ang)} r={stroke / 2 - 2} fill="#fff" opacity="0.85" /> : null}
      </svg>
      </div>
      <div className="ring-center">{children}</div>
    </div>
  );
}

/**
 * Area chart: gradient fill, glowing line, pulsing end point with a value badge, crosshair + tooltip on
 * hover/touch. `dashed` marks a projection rather than history.
 */
export function AreaChart({
  points,
  label,
  formatX,
  formatY,
  height = 190,
  dashed,
  badge,
  arrow,
  ticks,
}: {
  points: XY[];
  label: string;
  formatX(x: number): string;
  formatY(y: number): string;
  height?: number;
  dashed?: boolean;
  badge?: string;
  arrow?: boolean;
  ticks?: { x: number; label: string }[];
}) {
  const id = useId().replace(/:/g, '');
  const [box, W] = useWidth<HTMLDivElement>(360);
  const H = height;
  const pad = { l: 46, r: 18, t: 22, b: 26 };
  const geo = useMemo(() => {
    if (points.length < 2 || !points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return null;
    const s = scales(points, W, H, pad);
    const pts = points.map((p) => [s.sx(p.x), s.sy(p.y)] as [number, number]);
    const line = monotonePath(pts);
    const area = `${line}L${pts[pts.length - 1][0].toFixed(1)},${H - pad.b}L${pts[0][0].toFixed(1)},${H - pad.b}Z`;
    const grid = niceTicks(s.y0, s.y1, 4);
    return { ...s, pts, xs: pts.map((q) => q[0]), line, area, grid };
  }, [points, H, W]); // eslint-disable-line react-hooks/exhaustive-deps
  const { ref, hover: rawHover, handlers } = useHover(geo?.xs ?? null, W);
  if (!geo) return <div ref={box} className="area-chart" style={{ height: H }} />;
  const hover = rawHover !== null && rawHover < geo.pts.length ? rawHover : null;
  const end = geo.pts[geo.pts.length - 1];
  const h = hover !== null ? geo.pts[hover] : null;
  const key = points.map((p) => `${p.x}:${p.y}`).join('|');
  return (
    <div className="area-chart" ref={box}>
      <svg
        ref={ref}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        key={key}
        {...handlers}
      >
        <defs>
          <linearGradient id={`af${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8340fa" stopOpacity="0.55" />
            <stop offset="0.6" stopColor="#673df9" stopOpacity="0.16" />
            <stop offset="1" stopColor="#673df9" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`al${id}`} x1="0" x2="1">
            <stop offset="0" stopColor="#6d3cf5" />
            <stop offset="1" stopColor="#d94fe9" />
          </linearGradient>
          <filter id={`ag${id}`} x="-10%" y="-30%" width="120%" height="160%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          {arrow ? (
            <marker id={`am${id}`} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse" markerUnits="strokeWidth">
              <path d="M0,0 L10,5 L0,10 z" fill="#d94fe9" />
            </marker>
          ) : null}
        </defs>
        {geo.grid.map((g, i) => (
          <g key={i} className="fade-in">
            <line x1={pad.l} x2={W - pad.r} y1={geo.sy(g)} y2={geo.sy(g)} stroke="var(--chart-grid)" strokeDasharray="3 5" />
            <text className="axis" x={pad.l - 8} y={geo.sy(g) + 4} textAnchor="end">
              {formatY(g)}
            </text>
          </g>
        ))}
        <path className="area-rise" d={geo.area} fill={`url(#af${id})`} />
        <path className="draw" pathLength={1} d={geo.line} fill="none" stroke={`url(#al${id})`} strokeWidth="6" opacity="0.4" filter={`url(#ag${id})`} />
        <path
          className={dashed ? 'draw dashed' : 'draw'}
          pathLength={1}
          d={geo.line}
          fill="none"
          stroke={`url(#al${id})`}
          strokeWidth={arrow ? 3 : 2.5}
          strokeLinecap="round"
          markerEnd={arrow ? `url(#am${id})` : undefined}
        />
        {(ticks ?? [points[0], points[points.length - 1]].map((p) => ({ x: p.x, label: formatX(p.x) }))).map((t, i, all) => (
          <text key={i} className="axis fade-in" x={geo.sx(t.x)} y={H - 6} textAnchor={i === 0 && all.length > 1 ? 'start' : i === all.length - 1 ? 'end' : 'middle'}>
            {t.label}
          </text>
        ))}
        {arrow ? null : (
          <>
            <circle className="pop-late" cx={end[0]} cy={end[1]} r="4.5" fill="#e684f5" stroke="var(--surface)" strokeWidth="2" />
            <circle className="pulse" cx={end[0]} cy={end[1]} r="4.5" fill="none" stroke="#e684f5" strokeWidth="1.5" />
          </>
        )}
        {h ? (
          // the crosshair and marker glide between points (CSS transform transition) instead of jumping
          <g className="crosshair" data-testid="crosshair" style={{ transform: `translate(${h[0]}px, 0px)` }}>
            <line x1={0} x2={0} y1={pad.t} y2={H - pad.b} stroke="rgba(245,243,255,0.3)" strokeDasharray="2 3" />
            <circle className="crosshair-dot" cx={0} cy={0} r="5" fill="#f4f3ff" stroke="#d94fe9" strokeWidth="2.5" style={{ transform: `translate(0px, ${h[1]}px)` }} />
          </g>
        ) : null}
        <rect x="0" y="0" width={W} height={H} fill="transparent" />
      </svg>
      {badge && !h ? (
        <span className="chart-badge" style={{ left: `${(end[0] / W) * 100}%`, top: `${(end[1] / H) * 100}%` }}>
          {badge}
        </span>
      ) : null}
      {h && hover !== null ? (
        <div
          className="chart-tip glide"
          role="status"
          data-testid="chart-tip"
          style={{ left: `${(h[0] / W) * 100}%`, top: `${(h[1] / H) * 100}%`, transform: tipPlacement(h[0], h[1], W) }}
        >
          <span className="muted">{formatX(points[hover].x)}</span> · <b className="num">{formatY(points[hover].y)}</b>
        </div>
      ) : null}
      <SrTable label={label} rows={points.map((p) => [formatX(p.x), formatY(p.y)])} />
    </div>
  );
}

export interface Slice {
  key: string;
  label: ReactNode;
  value: number;
  display: ReactNode;
  color: string;
}

/** Donut whose slices sweep in one after another; hovering a slice or its legend row highlights it. */
export function Donut({ slices, center, label, size = 168, stroke = 26 }: { slices: Slice[]; center: ReactNode; label: string; size?: number; stroke?: number }) {
  const id = useId().replace(/:/g, '');
  const [active, setActive] = useState<string | null>(null);
  const total = slices.reduce((a, s) => a + (Number.isFinite(s.value) ? Math.max(0, s.value) : 0), 0);
  const r = (size - stroke) / 2 - 4;
  const c = size / 2;
  const C = 2 * Math.PI * r;
  const gap = total > 0 && slices.filter((s) => s.value > 0).length > 1 ? 3 : 0; // px of surface between fills
  let acc = 0;
  return (
    <div className="donut-wrap">
      <div className="donut" style={{ width: size, height: size }} data-lean="vars">
        {/* a solid ring of slices: drawn again below itself in darker steps, then tilted back */}
        <div className="depth-body donut-body">
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={label}>
          <defs>
            <filter id={`dd${id}`}>
              <feColorMatrix values="0.42 0 0 0 0  0 0.36 0 0 0  0 0 0.5 0 0  0 0 0 1 0" />
            </filter>
          </defs>
          {DEPTH.map((k) => {
            let at = 0;
            return (
              <g key={k} transform={`translate(0 ${k * 1.5}) rotate(-90 ${c} ${c})`} filter={`url(#dd${id})`} opacity={1 - k * 0.06}>
                <circle cx={c} cy={c} r={r} fill="none" stroke="#2c1f5c" strokeWidth={stroke} />
                {total > 0
                  ? slices.map((s, i) => {
                      const len = ((Number.isFinite(s.value) ? Math.max(0, s.value) : 0) / total) * C;
                      const off = at;
                      at += len;
                      if (len <= 0) return null;
                      const seg = Math.max(0, len - gap);
                      return (
                        <circle key={s.key} className="donut-seg depth" cx={c} cy={c} r={r} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={`${seg} ${C}`} strokeDashoffset={-off} style={{ animationDelay: `${i * 220}ms`, ['--seg' as string]: `${seg}` }} />
                      );
                    })
                  : null}
              </g>
            );
          })}
          <circle cx={c} cy={c} r={r} fill="none" stroke="#241848" strokeWidth={stroke} />
          <g transform={`rotate(-90 ${c} ${c})`}>
            {total > 0
              ? slices.map((s, i) => {
                  const len = ((Number.isFinite(s.value) ? Math.max(0, s.value) : 0) / total) * C;
                  const off = acc;
                  acc += len;
                  if (len <= 0) return null;
                  const seg = Math.max(0, len - gap);
                  return (
                    <circle
                      key={s.key}
                      className={`donut-seg${active && active !== s.key ? ' dim' : ''}${active === s.key ? ' on' : ''}`}
                      cx={c}
                      cy={c}
                      r={r}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={stroke}
                      strokeDasharray={`${seg} ${C}`}
                      strokeDashoffset={-off}
                      style={{ animationDelay: `${i * 220}ms`, ['--seg' as string]: `${seg}` }}
                      onMouseEnter={() => setActive(s.key)}
                      onMouseLeave={() => setActive(null)}
                    />
                  );
                })
              : null}
          </g>
        </svg>
        </div>
        <div className="donut-center">{center}</div>
      </div>
      <ul className="legend">
        {slices.map((s) => (
          <li key={s.key} onMouseEnter={() => setActive(s.key)} onMouseLeave={() => setActive(null)} className={active === s.key ? 'on' : ''}>
            <span className="sw" style={{ background: s.color }} />
            <span className="lg-text">
              <span className="soft">{s.label}</span>
              <b className="num">{s.display}</b>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The dash pattern that lights the first `n` dashes of the r 101 ring (97 dashes of 2.2 px), then nothing. */
function litPattern(n: number): string {
  const on = Math.min(0.95, 2.2 / ((2 * Math.PI * 101) / 97));
  return `${Array.from({ length: n }, () => `${on.toFixed(3)} ${(1 - on).toFixed(3)}`).join(' ')} 0 97`;
}

/**
 * A dotted ring with exactly `count` dashes of `dash` px (pathLength = count, so the spacing never rounds), each dash
 * carrying the small soft halo the reference shows (a blurred copy under the crisp one).
 */
function DotRing({ r, count, dash, width, color, opacity = 1, round, halo }: { r: number; count: number; dash: number; width: number; color: string; opacity?: number; round?: boolean; halo: string }) {
  const period = (2 * Math.PI * r) / count;
  const on = round ? 0.001 : Math.min(0.95, dash / period);
  const common = { cx: 128, cy: 128, r, fill: 'none', pathLength: count, strokeDasharray: `${on} ${1 - on}`, strokeLinecap: round ? ('round' as const) : ('butt' as const) };
  return (
    <>
      <circle {...common} stroke={color} strokeOpacity={opacity * 0.45} strokeWidth={width + 1} filter={`url(#${halo})`} />
      <circle {...common} stroke={color} strokeOpacity={opacity} strokeWidth={width} />
    </>
  );
}

/** What the staking orb shows, all from the balance card it sits in. */
export interface OrbData {
  staked?: number; // the staked balance beside it (sSYN)
  rebaseProgress?: number; // 0..1 through the current 12 h epoch
  epoch?: number; // changes at every rebase
  stakedShare?: number; // 0..1 of the supply staked
  rate?: number; // reward per epoch
  maxRate?: number; // the cap per epoch
}
const clamp01 = (v: number | undefined) => (v !== undefined && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0);
/** The core's size for a staked balance: 0.6 empty, growing by order of magnitude up to 1.15 at 100k. */
export function orbCoreScale(staked: number | undefined): number {
  const v = staked !== undefined && Number.isFinite(staked) && staked > 0 ? staked : 0;
  return 0.6 + 0.55 * Math.min(1, Math.log10(1 + v) / 5);
}
/** How fast the rings tumble for a reward rate: 0.5× with no rewards, 1.5× at the per-epoch cap. */
export function orbPace(rate: number | undefined, maxRate: number | undefined): number {
  if (!rate || !maxRate || !Number.isFinite(rate) || !Number.isFinite(maxRate) || maxRate <= 0) return 1;
  return 0.5 + Math.min(1, Math.max(0, rate / maxRate));
}
/** How many of a dotted ring's `count` dashes are lit for a share. */
export const litDashes = (share: number | undefined, count: number) => Math.round(clamp01(share) * count);

/**
 * The staking orb, cloned from the client's reference and built as a 3D model whose parts show the staking figures
 * beside it:
 * - the core grows and brightens with the staked balance;
 * - the main ring is the rebase clock: a bright arc sweeps clockwise from the top through the 12 h epoch, and at each
 *   rebase the core sends out a shockwave;
 * - the outer dotted ring lights as many of its dots as the share of the supply that is staked;
 * - the inner rings tumble faster the higher the reward rate is against its cap.
 * The scene sways and leans towards the pointer. Under reduced motion the rings rest facing the viewer, the client's
 * flat design, still showing the figures.
 *
 * The parts were measured from the image (256-unit box, centre fitted on the main ring, every radius sampled 1,440
 * times):
 * - core: white only to r 3, then #fdc1fb (4) → #f573f6 (6) → #e12ff2 (8) → #c10aea (10) → #a100dd (12) →
 *   #6b00b4 (16) → #47008b (20) → #2d0064 (28), with a cross lens flare;
 * - background: dark violet #250059 between the rings, #33026e just inside the main ring, #180641 → #130737 outside;
 * - solid rings: r 51.5 a soft 2 px line peaking at #9b46ba whose glow dies within ~3 px; r 85 the main ring, a
 *   2.5 px line peaking at #e698ee over a pure-violet glow (#8f0dc6 → #6100a6) reaching ~6 px either side;
 * - dotted rings with soft halos: r 26.5 (14 faint dots), r 37 (25 × 2 px), r 67 (61 × 1.8 px), r 101 (97 × 2.2 px),
 *   and 20 dim violet dots at r 115; plus a few particles.
 */
export function Orb({ size = 220, data = {}, label }: { size?: number; data?: OrbData; label?: string }) {
  const id = useId().replace(/:/g, '');
  const progress = clamp01(data.rebaseProgress);
  const lit = litDashes(data.stakedShare, 97);
  const coreScale = orbCoreScale(data.staked);
  // a rebase (the epoch moving on) sends a shockwave out of the core; the first reading does not
  const lastEpoch = useRef(data.epoch);
  const [shock, setShock] = useState(0);
  useEffect(() => {
    if (data.epoch !== undefined && lastEpoch.current !== undefined && data.epoch !== lastEpoch.current) setShock((n) => n + 1);
    lastEpoch.current = data.epoch;
  }, [data.epoch]);
  const c = 128;
  const halo = `oh${id}`;
  const tilt = useRef<HTMLDivElement>(null);
  // particles around the model, at different depths: [x, y, z] in 256 units from the centre, size, delay s
  const sparks = [
    [-95, -80, 40, 1.9, 0], [-30, -118, -30, 1.4, 1.1], [100, -66, 20, 2.0, 0.5], [118, 30, -45, 1.6, 1.6],
    [70, 100, 50, 1.4, 0.8], [-40, 116, -20, 1.9, 2.2], [-118, 20, 30, 1.5, 0.3], [20, -40, 70, 1.2, 1.4],
    [-60, 50, -70, 1.3, 0.9], [44, 60, -60, 1.1, 1.9],
  ] as const;
  const k = size / 256; // px per unit
  // one ring plane: an SVG the size of the model, so every ring keeps its measured radius
  const plane = (cls: string, body: ReactNode, z = 0) => (
    <div className={`orb3d-plane ${cls}`} style={z ? { translate: `0 0 ${z * k}px` } : undefined}>
      <svg viewBox="0 0 256 256" width={size} height={size}>
        {body}
      </svg>
    </div>
  );
  const mainRing = (
    <>
      <circle cx={c} cy={c} r="85" fill="none" stroke="#8a06cc" strokeWidth="11" opacity="0.78" filter={`url(#gw${id})`} />
      <circle cx={c} cy={c} r="85" fill="none" stroke="#b23ad8" strokeWidth="4.6" opacity="0.95" filter={`url(#gm${id})`} />
      <circle cx={c} cy={c} r="85" fill="none" stroke="#f2acf3" strokeWidth="2.1" filter={`url(#gs${id})`} />
      <circle cx={c} cy={c} r="85" fill="none" stroke="#ffe0fd" strokeWidth="0.9" opacity="0.6" />
    </>
  );
  const tubeLayer = <circle cx={c} cy={c} r="85" fill="none" stroke="#c45be6" strokeWidth="2.4" opacity="0.55" filter={`url(#gs${id})`} />;
  const lean = (e: PointerEvent<HTMLDivElement>) => {
    const el = tilt.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.style.setProperty('--ly', `${(((e.clientX - r.left) / r.width - 0.5) * 30).toFixed(1)}deg`);
    el.style.setProperty('--lx', `${(((e.clientY - r.top) / r.height - 0.5) * -30).toFixed(1)}deg`);
  };
  const unlean = () => {
    tilt.current?.style.removeProperty('--lx');
    tilt.current?.style.removeProperty('--ly');
  };
  return (
    <div
      className="orb3d"
      style={{ width: size, height: size * 1.22, ['--pace' as string]: orbPace(data.rate, data.maxRate).toFixed(3) }}
      {...(label ? { role: 'img', 'aria-label': label, title: label } : { 'aria-hidden': true })}
      data-testid="orb"
      data-progress={progress.toFixed(3)}
      data-lit={lit}
      onPointerMove={lean}
      onPointerLeave={unlean}
    >
      {/* the orb's light on the card around it */}
      <span className="orb3d-spill" />
      {/* the base it floats over: a glowing disc lying on the card, with a faint beam up to the core */}
      <span className="orb3d-beam" />
      <span className="orb3d-base">
        <i className="b-glow" />
        <i className="b-ring r1" />
        <i className="b-ring r2" />
        <i className="b-ring r3" />
      </span>
      <div className="orb3d-body" style={{ height: size }}>
      {/* the violet field behind the model, and the shared gradients and glows */}
      <svg className="orb3d-field" viewBox="0 0 256 256" width={size} height={size}>
        <defs>
          {/* the reference's violet between the rings, fading smoothly into the card (no dark disc edge) */}
          <radialGradient id={`bg${id}`} cx="128" cy="128" r="128" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#3a0a80" />
            <stop offset="0.3" stopColor="#2e0670" stopOpacity="0.95" />
            <stop offset="0.6" stopColor="#3b0884" stopOpacity="0.75" />
            <stop offset="0.72" stopColor="#33067a" stopOpacity="0.5" />
            <stop offset="0.86" stopColor="#24055a" stopOpacity="0.2" />
            <stop offset="1" stopColor="#24055a" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`core${id}`} cx="128" cy="128" r="32" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.07" stopColor="#fcf4fc" />
            <stop offset="0.11" stopColor="#fdc1fb" />
            <stop offset="0.17" stopColor="#f573f6" />
            <stop offset="0.235" stopColor="#e12ff2" />
            <stop offset="0.295" stopColor="#c10aea" />
            <stop offset="0.375" stopColor="#a100dd" />
            <stop offset="0.5" stopColor="#6b00b4" />
            <stop offset="0.625" stopColor="#47008b" />
            <stop offset="0.75" stopColor="#360072" stopOpacity="0.85" />
            <stop offset="0.875" stopColor="#2d0064" stopOpacity="0.5" />
            <stop offset="1" stopColor="#250059" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`fh${id}`} x1="0" x2="1">
            <stop offset="0" stopColor="#f6c8ff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="1" stopColor="#f6c8ff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`fv${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f6c8ff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="1" stopColor="#f6c8ff" stopOpacity="0" />
          </linearGradient>
          <filter id={`gw${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>
          <filter id={`gm${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1" />
          </filter>
          <filter id={`gs${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.45" />
          </filter>
          <filter id={halo} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.9" />
          </filter>
        </defs>
        <circle cx={c} cy={c} r="128" fill={`url(#bg${id})`} className="orb3d-aura" />
      </svg>

      <div className="orb3d-lean" ref={tilt}>
        <div className="orb3d-scene">
          {/* the outer dotted rings (r 115: 20 dots, r 101: 97 dashes): a disc set back, turning in its plane */}
          {plane(
            'disc',
            <>
              <g className="orb3d-turn rev">
                <DotRing r={115} count={20} dash={0} width={2.1} color="#a65ad6" opacity={1} round halo={halo} />
              </g>
              {/* the r 101 ring holds still, so its lit dots start at the top like the rebase clock */}
              <g>
                <DotRing r={101} count={97} dash={2.2} width={1.4} color="#a274d6" opacity={0.66} halo={halo} />
                {/* the share of the supply staked: that many dots lit, clockwise from the top */}
                {lit > 0 ? (
                  <g transform={`rotate(-90 ${c} ${c})`}>
                    <circle cx={c} cy={c} r={101} fill="none" pathLength={97} stroke="#f0abfc" strokeWidth={5} strokeOpacity={0.6} filter={`url(#gm${id})`} strokeDasharray={litPattern(lit)} />
                    <circle cx={c} cy={c} r={101} fill="none" pathLength={97} stroke="#fff4ff" strokeWidth={2.4} strokeDasharray={litPattern(lit)} className="orb3d-lit" />
                  </g>
                ) : null}
              </g>
            </>,
            -26,
          )}
          {/* the main ring (r 85), a glowing tube that is the rebase clock: it faces the viewer, and a bright arc
              sweeps clockwise from the top as the epoch runs, its head marking now */}
          <div className="orb3d-clock">
            {[-3, -2, -1, 1, 2, 3].map((z) => (
              <Fragment key={z}>{plane('tube', tubeLayer, z)}</Fragment>
            ))}
            {plane('tube dim', mainRing)}
            {plane(
              'arc',
              <g transform={`rotate(-90 ${c} ${c})`}>
                <circle cx={c} cy={c} r="85" fill="none" pathLength={1} stroke="#e879f9" strokeWidth="8" opacity="0.55" filter={`url(#gw${id})`} strokeDasharray={`${progress} 1`} className="orb3d-arc" />
                <circle cx={c} cy={c} r="85" fill="none" pathLength={1} stroke="#ffe8fe" strokeWidth="2.8" strokeLinecap="round" strokeDasharray={`${progress} 1`} className="orb3d-arc" />
                {progress > 0 ? (
                  <g className="orb3d-head" style={{ transform: `rotate(${progress * 360}deg)` }}>
                    <circle cx={c + 85} cy={c} r="6" fill="#f0abfc" opacity="0.6" filter={`url(#gm${id})`} />
                    <circle cx={c + 85} cy={c} r="3" fill="#fff" />
                  </g>
                ) : null}
              </g>,
              3.5,
            )}
          </div>
          {/* the dotted ring (r 67) tumbling about the horizontal axis */}
          <div className="orb3d-gimbal g-mid">{plane('', <DotRing r={67} count={61} dash={1.8} width={1.3} color="#9c56c4" opacity={0.64} halo={halo} />)}</div>
          {/* the thin solid ring (r 51.5) on a diagonal axis */}
          <div className="orb3d-gimbal g-inner">
            {plane(
              '',
              <>
                <circle cx={c} cy={c} r="51.5" fill="none" stroke="#7a24ae" strokeWidth="5" opacity="0.7" filter={`url(#gm${id})`} />
                <circle cx={c} cy={c} r="51.5" fill="none" stroke="#a84cc4" strokeWidth="2" filter={`url(#gs${id})`} />
                <circle cx={c} cy={c} r="51.5" fill="none" stroke="#d58ae6" strokeWidth="0.8" opacity="0.5" />
              </>,
            )}
          </div>
          {/* the inner dotted rings (r 37: 25 dashes, r 26.5: 14 dots), turning the other way */}
          <div className="orb3d-gimbal g-core">
            {plane(
              '',
              <>
                <DotRing r={37} count={25} dash={2} width={1.4} color="#8a44c0" opacity={0.42} halo={halo} />
                <DotRing r={26.5} count={14} dash={1.8} width={1.2} color="#7d3cb0" opacity={0.35} halo={halo} />
              </>,
            )}
          </div>
          {/* the core always faces the viewer: white to r 3, then magenta into violet, with the cross flare */}
          {plane(
            'core',
            <>
              {/* the core, as large as the staked balance */}
              <g transform={`translate(${c} ${c}) scale(${coreScale.toFixed(3)}) translate(${-c} ${-c})`}>
                <circle cx={c} cy={c} r="32" fill={`url(#core${id})`} className="orb3d-pulse" />
              </g>
              {shock ? <circle key={shock} cx={c} cy={c} r="30" fill="none" stroke="#f5c2ff" strokeWidth="2.5" className="orb3d-shock" /> : null}
              <g className="orb3d-flare">
                <rect x={c - 92} y={c - 0.45} width="184" height="0.9" fill={`url(#fh${id})`} opacity="0.5" />
                <rect x={c - 0.45} y={c - 92} width="0.9" height="184" fill={`url(#fv${id})`} opacity="0.5" />
                <path d={`M${c} ${c - 11}L${c + 1.3} ${c - 1.3}L${c + 11} ${c}L${c + 1.3} ${c + 1.3}L${c} ${c + 11}L${c - 1.3} ${c + 1.3}L${c - 11} ${c}L${c - 1.3} ${c - 1.3}Z`} fill="#ffffff" opacity="0.7" />
              </g>
            </>,
          )}
          {/* particles floating at different depths */}
          {sparks.map(([x, y, z, s, d], i) => (
            <i
              key={i}
              className="orb3d-spark"
              style={{ width: s * 3.4 * k, height: s * 3.4 * k, margin: `${-s * 1.7 * k}px 0 0 ${-s * 1.7 * k}px`, transform: `translate3d(${x * k}px, ${y * k}px, ${z * k}px)`, animationDelay: `${d}s` }}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
